import { HStack, Text, Badge, Flex, Box, Icon, Hide } from '@chakra-ui/react';
import {
  formatNextViewing,
  getNextViewing,
  isInspected,
  isInsured,
} from '@habity/services';
import { NestedHome } from '@habity/types';
import { useTranslations } from 'next-intl';
import { ICONS } from '~/constants';

interface HomeProfileOverlayBadgesProps {
  home: NestedHome;
  showStatusBadge?: boolean;
  showBiddingBadge?: boolean;
  showNextViewingBadge?: boolean;
  showInspectedBadge?: boolean;
  showPremarketHomesLocked?: boolean;
  showDebugBadge?: boolean;
}

export function HomeProfileOverlayBadges({
  home,
  showStatusBadge = true,
  showBiddingBadge = true,
  showNextViewingBadge = true,
  showInspectedBadge = true,
  showPremarketHomesLocked = false,
  showDebugBadge = false,
}: HomeProfileOverlayBadgesProps) {
  const { viewings, is_bidding_active, status, is_hidden } = home || {};
  const { show_status_badge } = status || {};
  const nextViewing = getNextViewing(viewings);
  const t = useTranslations();

  return (
    <Flex
      direction={{ mobile: 'column', desktop: 'row' }}
      gap={{ mobile: 1.5, desktop: 3 }}
    >
      {/* Inspected */}
      {(isInspected(home) || isInsured(home)) && showInspectedBadge && (
        <Box>
          <Badge variant="info">
            <HStack gap="0.5" ml="-6px">
              <Icon as={ICONS.CHECK} boxSize="5" />
              <Text size="sm" fontWeight="medium">
                {isInspected(home) && isInsured(home)
                  ? t('HomeProfile.Inspected') // Using just the inspected text as Marcus suggested
                  : isInspected(home)
                  ? t('HomeProfile.Inspected')
                  : t('HomeProfile.Insured')}
              </Text>
            </HStack>
          </Badge>
        </Box>
      )}

      {/* Status badge */}
      {((show_status_badge && showStatusBadge) || showDebugBadge) && (
        <Box>
          <Badge variant="info">
            <HStack>
              {showPremarketHomesLocked && status?.show_public_preview && (
                <Icon as={ICONS.EDIT_HOME_LOCKED} boxSize="3.5" />
              )}
              <Text size="sm" fontWeight="medium">
                {t(`Status.${status?.name}.title`)}
              </Text>
            </HStack>
          </Badge>
        </Box>
      )}

      {is_hidden && (
        <Box>
          <Badge variant="info">
            <HStack gap="1">
              <Icon as={ICONS.HIDE_DETAILS} boxSize="4" />
              <Text size="sm" fontWeight="medium">
                {t(`HomeProfile.Hidden`)}
              </Text>
            </HStack>
          </Badge>
        </Box>
      )}

      {/* Next viewing badge */}
      {nextViewing && showNextViewingBadge && (
        <Box>
          <Badge variant="info">
            <HStack gap="2">
              <Hide below="tablet">
                <Icon as={ICONS.CALENDAR} boxSize="4" />
              </Hide>
              <Text size="sm" fontWeight="medium">
                {formatNextViewing(nextViewing)}
              </Text>
            </HStack>
          </Badge>
        </Box>
      )}

      {/* Bidding badge */}
      {is_bidding_active && showBiddingBadge && (
        <Box>
          <Badge variant="info">
            <Text size="sm" fontWeight="medium">
              {t('Home.PriceDescription.bidding-active')}
            </Text>
          </Badge>
        </Box>
      )}
    </Flex>
  );
}
